import { useRef } from "react";
import * as RadixDialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { IconButton } from "./Button";
import cn from "./cn";

// Radix supplies the behaviour the 40 hand-rolled modals lack: a portal, focus
// trap, initial focus, Escape and inert background.

// Radix returns focus only to its own Dialog.Trigger. Our dialogs are opened
// by ordinary buttons, so remember whatever had focus when the dialog opened
// and put focus back there when it closes.
function useReturnFocus({ focusFirstField = false } = {}) {
  const opener = useRef(null);
  return {
    onOpenAutoFocus: (event) => {
      opener.current = document.activeElement;
      // A dialog that asks for input starts on its first field, not on Close.
      if (!focusFirstField) return;
      const field = event.currentTarget?.querySelector?.(
        "[data-dialog-body] input:not([type=hidden]):not([disabled]), [data-dialog-body] textarea:not([disabled]), [data-dialog-body] select:not([disabled])"
      );
      if (field) {
        event.preventDefault();
        field.focus();
      }
    },
    onCloseAutoFocus: (event) => {
      const element = opener.current;
      opener.current = null;
      if (element && element !== document.body && document.contains(element)) {
        event.preventDefault();
        element.focus();
      }
    },
  };
}

const SIZES = { sm: "max-w-sm", md: "max-w-lg", lg: "max-w-2xl", wide: "max-w-3xl", xl: "max-w-5xl" };

const overlay = "fixed inset-0 z-50 bg-black/40 motion-safe:data-[state=open]:animate-fade-in";

/**
 * Dialog — a labelled modal. Escape closes it. An outside click does not, by
 * default, so a half-filled form is never lost by a stray click; pass
 * `closeOnOutsideClick` for read-only dialogs.
 */
export default function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = "md",
  closeOnOutsideClick = false,
  closeLabel = "Close",
  className,
}) {
  const returnFocus = useReturnFocus({ focusFirstField: true });
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className={overlay} />
        <RadixDialog.Content
          data-ui="dialog"
          {...returnFocus}
          {...(description ? {} : { "aria-describedby": undefined })}
          onPointerDownOutside={closeOnOutsideClick ? undefined : (event) => event.preventDefault()}
          className={cn(
            "fixed left-1/2 top-1/2 z-50 flex max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col",
            "rounded-dialog border border-default bg-surface-raised text-foreground shadow-dialog focus:outline-none",
            SIZES[size],
            className
          )}
        >
          <div className="flex items-start justify-between gap-4 px-6 pb-2 pt-5">
            <div className="min-w-0">
              <RadixDialog.Title className="text-section-title text-foreground">{title}</RadixDialog.Title>
              {description ? (
                <RadixDialog.Description className="mt-1 text-body-sm text-secondary">{description}</RadixDialog.Description>
              ) : null}
            </div>
            <RadixDialog.Close asChild>
              <IconButton icon={X} label={closeLabel} size="sm" className="-mr-2 -mt-1" />
            </RadixDialog.Close>
          </div>
          <div data-dialog-body className="min-h-0 flex-1 overflow-y-auto px-6 py-3 text-body">{children}</div>
          {footer ? (
            <div className="flex flex-col-reverse gap-2 border-t border-subtle px-6 py-4 sm:flex-row sm:justify-end">{footer}</div>
          ) : null}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

/** Closes the surrounding Dialog; wrap the footer's cancel Button. */
export const DialogClose = RadixDialog.Close;

// Where a sheet sits. `detail` is a row's or a day's detail: a panel on the right edge on desktop and a bottom sheet
// on phones, where the thumb reaches it.
const SHEET_SIDES = {
  left: "inset-y-0 left-0 w-[min(86vw,300px)] border-r motion-safe:data-[state=open]:animate-sheet-in",
  detail: cn(
    "inset-x-0 bottom-0 max-h-[85dvh] rounded-t-dialog border-t motion-safe:data-[state=open]:animate-fade-in",
    "sm:inset-x-auto sm:inset-y-0 sm:right-0 sm:max-h-none sm:w-[min(92vw,440px)] sm:rounded-none sm:border-l sm:border-t-0"
  ),
};

/**
 * Sheet — the same accessible dialog anchored to an edge. `side="left"` (the default) is mobile navigation;
 * `side="detail"` is row or day detail (right panel on desktop, bottom sheet on phones). An outside tap closes it.
 */
export function Sheet({ open, onOpenChange, title, titleHidden = false, side = "left", children, className }) {
  const returnFocus = useReturnFocus();
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className={overlay} />
        <RadixDialog.Content
          data-ui="sheet"
          {...returnFocus}
          aria-describedby={undefined}
          data-side={side}
          className={cn(
            "fixed z-50 flex flex-col border-default bg-surface text-foreground shadow-dialog focus:outline-none",
            SHEET_SIDES[side] || SHEET_SIDES.left,
            className
          )}
        >
          <RadixDialog.Title className={titleHidden ? "sr-only" : "px-4 pt-4 text-section-title"}>{title}</RadixDialog.Title>
          {children}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

export const SheetClose = RadixDialog.Close;
